import {useEffect, useMemo, useState} from 'react'
import {Alert, Spinner} from 'react-bootstrap'
import {useMutation, useQuery} from '@apollo/client'
import {toast} from 'react-toastify'

import {
  GET_CAMPAIGNS,
  GET_CAMPAIGN,
  GET_CAMPAIGN_LOGS,
  GET_CAMPAIGN_TARGETS,
  GET_MESSAGE_TEMPLATES,
} from '../../gql/queries/campaigns'
import {
  ADD_CAMPAIGN_TARGETS,
  CREATE_CAMPAIGN,
  CREATE_MESSAGE_TEMPLATE,
  LAUNCH_CAMPAIGN,
  PAUSE_CAMPAIGN,
  SEND_CAMPAIGN_TEST_MESSAGE,
  UPDATE_CAMPAIGN,
  UPDATE_MESSAGE_TEMPLATE,
} from '../../gql/mutations/campaigns'
import {ADD_TO_RECIPIENTS} from '../../gql/mutations/recipients'
import {
  CampaignChannel,
  ICampaign,
  ICampaignLog,
  ICampaignRes,
  ICampaignsRes,
  ICampaignTarget,
  ICampaignTargetsRes,
  IMessageTemplate,
  IMessageTemplatesRes,
  ICampaignLogsRes,
} from '../../types/campaigns'
import {CampaignSidebar} from './components/CampaignSidebar'
import {CampaignDetail} from './components/CampaignDetail'
import {CampaignFormModal, CampaignFormValues} from './components/CampaignFormModal'
import {TemplateFormModal} from './components/TemplateFormModal'
import {AddTargetModal} from './components/AddTargetModal'

export const CampaignsPage = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('')
  const [campaignModalOpen, setCampaignModalOpen] = useState(false)
  const [templateModalOpen, setTemplateModalOpen] = useState(false)
  const [targetModalOpen, setTargetModalOpen] = useState(false)
  const [editingCampaign, setEditingCampaign] = useState<ICampaign | null>(null)
  const [editingTemplate, setEditingTemplate] = useState<IMessageTemplate | null>(null)

  const {
    data: campaignsData,
    loading: loadingCampaigns,
    error: campaignsError,
    refetch: refetchCampaigns,
  } = useQuery<ICampaignsRes>(GET_CAMPAIGNS, {
    variables: {includeArchived: false},
    fetchPolicy: 'cache-and-network',
  })

  const {data: templatesData, refetch: refetchTemplates} = useQuery<IMessageTemplatesRes>(GET_MESSAGE_TEMPLATES, {
    fetchPolicy: 'cache-and-network',
  })

  const campaigns = campaignsData?.campaigns ?? []
  const templates = templatesData?.messageTemplates ?? []
  const variantChannelMap = useMemo(
    () =>
      templates.reduce<Record<string, CampaignChannel>>((acc, template) => {
        template.variants?.forEach((variant) => {
          acc[variant.id] = variant.channel
        })
        return acc
      }, {}),
    [templates]
  )

  const decodeRelayId = (globalId: string) => {
    try {
      const decoded = atob(globalId)
      const parts = decoded.split(':')
      return parts.length > 1 ? parts[1] : globalId
    } catch (error) {
      return globalId
    }
  }

  useEffect(() => {
    if (!selectedCampaignId && campaigns.length) {
      setSelectedCampaignId(campaigns[0].id)
    }
  }, [campaigns, selectedCampaignId])

  const {
    data: campaignDetailData,
    loading: loadingCampaignDetail,
    refetch: refetchCampaign,
  } = useQuery<ICampaignRes>(GET_CAMPAIGN, {
    skip: !selectedCampaignId,
    variables: {id: selectedCampaignId},
    fetchPolicy: 'cache-and-network',
  })

  const {
    data: targetsData,
    loading: loadingTargets,
    refetch: refetchTargets,
  } = useQuery<ICampaignTargetsRes>(GET_CAMPAIGN_TARGETS, {
    skip: !selectedCampaignId,
    variables: {campaignId: selectedCampaignId},
    fetchPolicy: 'no-cache',
  })

  const {
    data: logsData,
    loading: loadingLogs,
    refetch: refetchLogs,
  } = useQuery<ICampaignLogsRes>(GET_CAMPAIGN_LOGS, {
    skip: !selectedCampaignId,
    variables: {campaignId: selectedCampaignId},
    fetchPolicy: 'no-cache',
  })

  const selectedSummary = useMemo(() => campaigns.find((campaign) => campaign.id === selectedCampaignId), [campaigns, selectedCampaignId])
  const selectedCampaign: ICampaign | undefined =
    campaignDetailData?.campaign ??
    (selectedSummary
      ? ({
          ...selectedSummary,
          targetSegment: {},
          metadata: selectedSummary.metadata ?? {},
        } as ICampaign)
      : undefined)

  const [createCampaignMutation, {loading: creatingCampaign}] = useMutation(CREATE_CAMPAIGN)
  const [updateCampaignMutation, {loading: updatingCampaign}] = useMutation(UPDATE_CAMPAIGN)
  const [createTemplateMutation, {loading: creatingTemplate}] = useMutation(CREATE_MESSAGE_TEMPLATE)
  const [updateTemplateMutation, {loading: updatingTemplate}] = useMutation(UPDATE_MESSAGE_TEMPLATE)
  const [addTargetsMutation, {loading: addingTarget}] = useMutation(ADD_CAMPAIGN_TARGETS)
  const [launchCampaignMutation, {loading: launchingCampaign}] = useMutation(LAUNCH_CAMPAIGN)
  const [pauseCampaignMutation, {loading: pausingCampaign}] = useMutation(PAUSE_CAMPAIGN)
  const [sendTestMutation] = useMutation(SEND_CAMPAIGN_TEST_MESSAGE)
  const [addToRecipientsMutation] = useMutation(ADD_TO_RECIPIENTS)

  const handleOpenCreateCampaign = () => {
    setEditingCampaign(null)
    setCampaignModalOpen(true)
  }

  const handleOpenEditCampaign = () => {
    if (!selectedCampaign) return
    setEditingCampaign(selectedCampaign)
    setCampaignModalOpen(true)
  }

  const handleSubmitCampaign = async (values: CampaignFormValues) => {
    const enabledChannels = Array.from(
      new Set(
        values.selectedVariantIds
          .map((id) => variantChannelMap[id])
          .filter((channel): channel is CampaignChannel => Boolean(channel))
      )
    )

    try {
      if (editingCampaign) {
        await updateCampaignMutation({
          variables: {
            id: editingCampaign.id,
            name: values.name,
            description: values.description,
            enabledChannels,
            scheduleType: values.scheduleType,
            scheduledTime: values.scheduledTime,
            recurringRule: values.recurringRule || null,
            targetSegment: values.targetSegment,
            templateId: values.templateId,
            metadata: values.metadata,
          },
        })
        toast.success('Campaign updated')
      } else {
        const {data} = await createCampaignMutation({
          variables: {
            name: values.name,
            description: values.description,
            enabledChannels,
            scheduleType: values.scheduleType,
            scheduledTime: values.scheduledTime,
            recurringRule: values.recurringRule || null,
            targetSegment: values.targetSegment,
            templateId: values.templateId,
            metadata: values.metadata,
          },
        })
        const createdId = data?.createCampaign?.campaign?.id
        if (createdId && values.recipientContactIds.length) {
          // Ensure contacts are flagged as recipients before adding targets
          await addToRecipientsMutation({
            variables: {contactIds: values.recipientContactIds},
          })
          await addTargetsMutation({
            variables: {
              campaignId: createdId,
              targets: values.recipientContactIds.map((contactId) => ({
                contactId: decodeRelayId(contactId),
                metadata: null,
                channelOverrides: null,
              })),
            },
          })
        }
        toast.success('Campaign created')
      }
      setCampaignModalOpen(false)
      await Promise.all([refetchCampaigns(), selectedCampaignId && refetchCampaign({id: selectedCampaignId})])
    } catch (error) {
      toast.error('There was a problem saving the campaign')
    }
  }

  const handleSubmitTemplate = async (payload: {
    name: string
    description?: string
    channel: CampaignChannel
    subject?: string
    body: string
    variables?: string[]
    metadata?: Record<string, any>
  }) => {
    try {
      if (editingTemplate) {
        await updateTemplateMutation({
          variables: {
            id: editingTemplate.id,
            ...payload,
          },
        })
        toast.success('Template updated')
      } else {
        await createTemplateMutation({
          variables: payload,
        })
        toast.success('Template created')
      }
      setTemplateModalOpen(false)
      await refetchTemplates()
    } catch (error) {
      toast.error('Something went wrong saving the template')
    }
  }

  const handleAddTarget = async (payload: {
    contactId: string
    metadata?: Record<string, any>
    channelOverrides?: Record<string, any>
  }) => {
    if (!selectedCampaignId) return
    try {
      await addTargetsMutation({
        variables: {
          campaignId: selectedCampaignId,
          targets: [
            {
              contactId: payload.contactId,
              metadata: payload.metadata,
              channelOverrides: payload.channelOverrides,
            },
          ],
        },
      })
      toast.success('Target added')
      setTargetModalOpen(false)
      await Promise.all([refetchCampaign({id: selectedCampaignId}), refetchTargets()])
    } catch (error) {
      toast.error('Unable to add the target')
    }
  }

  const handleLaunchCampaign = async () => {
    if (!selectedCampaignId) return
    try {
      await launchCampaignMutation({
        variables: {
          id: selectedCampaignId,
          runAsync: true,
        },
      })
      toast.success('Campaign queued for delivery')
      await Promise.all([refetchCampaigns(), refetchCampaign({id: selectedCampaignId}), refetchLogs()])
    } catch (error) {
      toast.error('Unable to launch campaign')
    }
  }

  const handlePauseCampaign = async () => {
    if (!selectedCampaignId) return
    try {
      await pauseCampaignMutation({
        variables: {id: selectedCampaignId},
      })
      toast.success('Campaign paused')
      await Promise.all([refetchCampaigns(), refetchCampaign({id: selectedCampaignId})])
    } catch (error) {
      toast.error('Unable to pause the campaign')
    }
  }

  const handleSendTest = async (contactId: string, channels: CampaignChannel[]) => {
    if (!selectedCampaign?.template?.id) {
      toast.error('Please select a template before sending tests')
      return
    }
    try {
      await sendTestMutation({
        variables: {
          templateId: selectedCampaign.template.id,
          contactId,
          channels,
        },
      })
      toast.success('Test message sent')
    } catch (error) {
      toast.error('Unable to send test message')
      throw error
    }
  }

  const campaignListLoading = loadingCampaigns || loadingCampaignDetail

  return (
    <div className='campaigns-page'>
      <div className='d-flex align-items-center justify-content-between mb-6 flex-wrap gap-3'>
        <div>
          <h1 className='page-title'>Messaging campaigns</h1>
          <div className='text-muted'>Coordinate SMS, email, WhatsApp, and Telegram flows from one workspace.</div>
        </div>
        {loadingCampaigns && <Spinner animation='border' size='sm' />}
      </div>
      {campaignsError && <Alert variant='danger'>Unable to load campaigns: {campaignsError.message}</Alert>}
      <div className='row g-5'>
        <div className='col-xxl-3 col-xl-4'>
          <CampaignSidebar
            campaigns={campaigns}
            selectedId={selectedCampaignId}
            loading={campaignListLoading}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onSelect={setSelectedCampaignId}
            onCreateCampaign={handleOpenCreateCampaign}
          />
        </div>
        <div className='col-xxl-9 col-xl-8'>
          <CampaignDetail
            campaign={selectedCampaign}
            targets={targetsData?.campaignTargets ?? []}
            logs={logsData?.campaignLogs ?? []}
            loadingTargets={loadingTargets}
            loadingLogs={loadingLogs}
            onEditCampaign={handleOpenEditCampaign}
            onAddTarget={() => {
              setTargetModalOpen(true)
            }}
            onLaunchCampaign={handleLaunchCampaign}
            onPauseCampaign={handlePauseCampaign}
            launching={launchingCampaign}
            pausing={pausingCampaign}
            onSendTest={handleSendTest}
            onEditTemplate={(template) => {
              setEditingTemplate(template)
              setTemplateModalOpen(true)
            }}
            onRefreshTargets={() => refetchTargets()}
            onRefreshLogs={() => refetchLogs()}
          />
        </div>
      </div>
      <CampaignFormModal
        show={campaignModalOpen}
        onHide={() => setCampaignModalOpen(false)}
        onSubmit={handleSubmitCampaign}
        templates={templates}
        loading={creatingCampaign || updatingCampaign}
        campaign={editingCampaign}
      />
      <TemplateFormModal
        show={templateModalOpen}
        onHide={() => setTemplateModalOpen(false)}
        onSubmit={handleSubmitTemplate}
        template={editingTemplate}
        loading={creatingTemplate || updatingTemplate}
      />
      <AddTargetModal
        show={targetModalOpen}
        onHide={() => setTargetModalOpen(false)}
        onSubmit={handleAddTarget}
        loading={addingTarget}
        campaignName={selectedCampaign?.name}
      />
    </div>
  )
}
